import { AccountTreeRounded } from '@mui/icons-material';
import { ImportType, ValidatedRow } from '../../api/importer.contracts';
import { ImportDefinition } from '../import-definition';
import { lime } from '@mui/material/colors';
import { AxiosProgressEvent } from 'axios';
import { importerService } from '../../api/importer.service';
import { ColDefOrGroup } from '@/lib/ag-grid/types';
import i18n from '@/lib/i18n/i18nConfig';
import { ApiResponse } from '@/lib/api/api-response';
import { isEmpty, isString } from 'lodash-es';

export interface ParentLocationImportSummary {
  counts: ParentLocationImportCounts[];
  locations: ParentLocationDto[];
}

export interface ParentLocationImportCounts {
  parentLocationCode: string;
  childLocationCount: number;
}

export interface ParentLocationDto extends ValidatedRow {
  parentLocationCode: string;
  parentLocationId: number;
  childLocationCode: string;
  childLocationId: number;
}

export class ParentLocationImport implements ImportDefinition<ParentLocationImportSummary, ParentLocationDto> {
  title = i18n.t('parent_location_import');
  description = i18n.t('import_parent_locations');
  icon = (<AccountTreeRounded sx={{ fontSize: 40 }} />);
  iconColor = lime[500];
  type = ImportType.PARENT_LOCATION;
  templateFileUrl = '/importers/parent-locations/parent_locations_import_template.csv';

  async preview(data: { file: File; onUploadProgress: (progressEvent: AxiosProgressEvent) => void }) {
    const response = await importerService.validateFile<ParentLocationImportSummary, ParentLocationDto>(
      this.type,
      data.file,
      data.onUploadProgress,
    );

    return response;
  }

  async import(payload: { summary: ParentLocationImportSummary }): Promise<ApiResponse<boolean>> {
    const response = await importerService.importLocationsParentLocations(payload.summary.locations);

    return response.processPayload((data) => response.isSuccess);
  }

  summaryColDefs: ColDefOrGroup<ParentLocationImportSummary>[] = [
    {
      field: 'locations',
      valueGetter: (params) => params.data?.locations.length,
      headerName: i18n.t('number_locations_to_assign_new_parent'),
    },
  ];
  summaryMaxHeightInPx = 200;
  summaryMaxWidthInPx = 400;

  errorRowColDefs: ColDefOrGroup<ParentLocationDto>[] = [
    {
      headerName: i18n.t('parent_location_code'),
      field: 'parentLocationCode',
    },
    {
      headerName: i18n.t('child_location_code'),
      field: 'childLocationCode',
    },
    {
      field: 'isValid',
      headerName: i18n.t('valid'),
      cellDataType: 'text',
      valueFormatter: (params) => (params.value ? i18n.t('valid') : i18n.t('invalid')),
      cellClassRules: {
        'text-green-500': (params) => params.value,
        'text-red-500': (params) => !params.value,
      },
    },
    {
      headerName: i18n.t('error_reason'),
      field: 'errorReason',
      valueFormatter: (params) => {
        if (isEmpty(params.value) || !isString(params.value)) {
          return '';
        }

        return i18n.t(params.value.toLowerCase());
      },
    },
  ];
}
